import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Box, Flex } from 'components/Layout/Layout';

import Input from 'components/Input/Input';
import RadioGroup from 'components/RadioGroup/RadioGroup';
import LoginView from 'components/LoginView/LoginView';
import Button from 'components/Button/Button';
import P from 'components/P/P';

import messages from './messages';
import { isEmailValid } from '../../utils/email';
import { useStores } from '../../store/useStores';

function SignupView() {
  const intl = useIntl();
  const { authStore } = useStores();

  const [username, setUsername] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [experience, setExperience] = useState('');
  const [type, setType] = useState('bachelor');

  const [signupCreated, setSignupCreated] = useState(false);
  const [error, setError] = useState(null);

  const [firstnameError, setFirstnameError] = useState(false);
  const [lastnameError, setLastnameError] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [experienceError, setExperienceError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add('blue');
    return () => {
      document.body.classList.remove('blue');
    };
  }, []);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();
      setError(null);

      const response = await authStore.signup({
        email: username.toLowerCase(),
        firstname,
        lastname,
        experience,
        type
      });

      if (response?.error) {
        setError(response.error.message);
      } else {
        setSignupCreated(true);
      }
    },
    [authStore, experience, firstname, lastname, type, username]
  );

  const title = intl.formatMessage(messages.signupTitle);
  const message = intl.formatMessage(messages.signupMessage);

  const isValid =
    username && firstname && lastname && experience && isEmailValid(username);

  return (
    <LoginView title={title} message={signupCreated ? '' : message}>
      {signupCreated && (
        <Box mb={10}>
          <P>
            <FormattedMessage {...messages.signupSuccessMessage} />
          </P>
        </Box>
      )}
      {!signupCreated && (
        <form onSubmit={handleSubmit}>
          {error && (
            <Box mb={10}>
              <P danger>{error}</P>
            </Box>
          )}

          <Box mb={20}>
            <Input
              name="firstname"
              onChange={e => setFirstname(e.target.value)}
              placeholder={intl.formatMessage(messages.signupFirstName)}
              value={firstname}
              type="text"
              onBlur={() => setFirstnameError(!firstname)}
              error={firstnameError}
            />
          </Box>
          <Box mb={20}>
            <Input
              name="lastname"
              onChange={e => setLastname(e.target.value)}
              placeholder={intl.formatMessage(messages.signupLastName)}
              value={lastname}
              type="text"
              onBlur={() => setLastnameError(!lastname)}
              error={lastnameError}
            />
          </Box>
          <Box mb={20}>
            <Input
              name="username"
              placeholder={intl.formatMessage(messages.signupUsername)}
              onChange={e => setUsername(e.target.value)}
              value={username}
              onBlur={() =>
                setUsernameError(!username || !isEmailValid(username))
              }
              type="text"
              error={usernameError}
            />
          </Box>

          <Flex flexWrap="wrap">
            <Box width={[1, 1, 1 / 2]} mb={2}>
              <Input
                name="experience"
                type="text"
                onChange={e => setExperience(e.target.value)}
                placeholder={intl.formatMessage(messages.loginExperience)}
                value={experience}
                onBlur={() => setExperienceError(!experience)}
                error={experienceError}
              />
            </Box>
            <Box width={[1, 1, 1 / 2]} pl={[0, 0, 2]} mb={2} pt={1}>
              <RadioGroup
                name="type"
                active={type}
                items={[
                  {
                    label: 'bachelor diploma',
                    value: 'bachelor'
                  },
                  {
                    label: 'master diploma',
                    value: 'master'
                  }
                ]}
                onChange={value => setType(value)}
              />
            </Box>
          </Flex>
          <Button disabled={!isValid} secondary>
            <FormattedMessage {...messages.signupSubmitButton} />
          </Button>
        </form>
      )}
    </LoginView>
  );
}

export default observer(SignupView);
